import { getAuthorDisplayName } from './displayName';

/** Token stored in post/reply text: @[Display Name](user:123) */
export const MENTION_TOKEN_RE = /@\[([^\]]+)\]\(user:(\d+)\)/g;

const mentionDirectory = new Map(); // id -> { id, name }

function mentionName(person) {
  return getAuthorDisplayName(person).replace(/[\[\]]/g, '').trim() || 'Member';
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function registerMentionPerson(person) {
  const id = Number(person?.id);
  if (!id) return;
  mentionDirectory.set(id, { id, name: mentionName(person) });
}

export function registerMentionPeople(people) {
  (people || []).forEach(registerMentionPerson);
}

/** Show @Name in composers instead of @[Name](user:123). */
export function toDisplayMentionText(text) {
  return String(text ?? '').replace(/@\[([^\]]+)\]\(user:\d+\)/g, '@$1');
}

/** Register ids from stored tokens and return the readable @Name form. */
export function hydrateMentionsFromText(text) {
  const source = String(text ?? '');
  const re = /@\[([^\]]+)\]\(user:(\d+)\)/g;
  let match;
  while ((match = re.exec(source)) !== null) {
    const id = Number(match[2]);
    if (id > 0) {
      mentionDirectory.set(id, { id, name: String(match[1]).trim() || 'Member' });
    }
  }
  return toDisplayMentionText(source);
}

/** Convert visible @Name mentions back to @[Name](user:id) for saving. */
export function toStoredMentionText(text) {
  let out = String(text ?? '');
  const people = [...mentionDirectory.values()].sort(
    (a, b) => b.name.length - a.name.length
  );
  for (const { id, name } of people) {
    if (!name) continue;
    const re = new RegExp(
      `(^|[^A-Za-z0-9_\\[])@${escapeRegExp(name)}(?!\\]\\(user:)`,
      'g'
    );
    out = out.replace(re, `$1@[${name}](user:${id})`);
  }
  return out;
}

/** Visible token inserted into the composer: @Display Name */
export function buildMentionToken(person) {
  if (!person?.id) return '';
  registerMentionPerson(person);
  return `@${mentionName(person)}`;
}

export function extractMentionIds(text) {
  if (!text) return [];
  const ids = [];
  const re = /@\[([^\]]+)\]\(user:(\d+)\)/g;
  const stored = toStoredMentionText(text);
  let match;
  while ((match = re.exec(stored)) !== null) {
    const id = Number(match[2]);
    if (id > 0) ids.push(id);
  }
  return [...new Set(ids)];
}

/** Append mention IDs to FormData for create/reply endpoints. */
export function appendMentionIds(formData, text, extraIds = []) {
  const ids = [
    ...new Set([
      ...extractMentionIds(text),
      ...((extraIds || []).map(Number).filter((id) => id > 0)),
    ]),
  ];
  formData.append('mentioned_user_ids', JSON.stringify(ids));
  ids.forEach((id) => formData.append('mentioned_user_ids[]', String(id)));
  return ids;
}

/** Write post/reply text plus mention IDs, encoding @Name to stored tokens. */
export function appendMentionedField(formData, fieldName, text, extraIds = []) {
  const stored = toStoredMentionText(text ?? '');
  formData.append(fieldName, stored);
  appendMentionIds(formData, stored, extraIds);
  return stored;
}

export function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Member-written text made safe to insert as HTML: tags are stripped (hiring /
 * article markup becomes plain text) and the rest is escaped. Every linkify
 * helper must start from this, or a post containing <img onerror=…> runs as script.
 */
export function escapeUserText(text) {
  let source = String(text ?? '');
  if (/<[a-z][\s\S]*>/i.test(source) || /<!--/.test(source)) {
    source = source
      .replace(/<!--[\s\S]*?-->/g, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/\s+/g, ' ')
      .trim();
  }
  return escapeHtml(source);
}

export function linkifyMentions(text) {
  if (!text) return '';
  return String(text).replace(
    /@\[([^\]]+)\]\(user:(\d+)\)/g,
    '<a href="/profile/$2" style="color:#2563eb;font-weight:600;text-decoration:none;">@$1</a>'
  );
}

/** Letters, numbers, and underscores after # — same rules as createLinkifiedText. */
export const HASHTAG_TOKEN_RE = /(^|\s)#([a-zA-Z0-9_]+)/g;

export function normalizeHashtag(raw) {
  const stripped = String(raw ?? '')
    .trim()
    .replace(/^#+/, '')
    .replace(/^%23/i, '');
  const cleaned = stripped.replace(/[^a-zA-Z0-9_]/g, '');
  return cleaned.slice(0, 80);
}

export function hashtagPath(tag) {
  const normalized = normalizeHashtag(tag);
  return normalized ? `/hashtag/${encodeURIComponent(normalized)}` : '/hashtag';
}

export function linkifyHashtags(text) {
  return String(text ?? '').replace(
    /(^|\s)#([a-zA-Z0-9_]+)/g,
    (full, lead, tag) =>
      `${lead}<a href="${hashtagPath(tag)}" style="color:#2563eb;text-decoration:none;">#${tag}</a>`
  );
}

/**
 * Shared linkify for posts/replies: mentions, URLs, hashtags.
 * Escapes HTML first so raw tags / hiring markup cannot break the layout.
 */
export function createLinkifiedText(text) {
  if (text == null || text === '') return '';

  let out = escapeUserText(text);
  out = linkifyMentions(out);
  out = out.replace(/(https?:\/\/(?:[^\s<]|&amp;)+)/g, (url) => {
    const href = url.replace(/&amp;/g, '&');
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#2563eb;word-break:break-all;">${url}</a>`;
  });
  out = linkifyHashtags(out);
  return out;
}
